/**
 * Resolve dialog rules (IMPLEMENTATION_PLAN §6, §11). The server is the
 * authority (it answers 422 `{ problems }`); this mirrors its guards so the
 * dialog can point at the missing field before the round trip.
 */
import { ApiError } from '@/api/request'

export const RESOLUTION_CATEGORIES = [
  'configuration_restored',
  'code_fix',
  'user_error_training',
  'data_corrected',
  'vendor_issue',
  'duplicate',
  'other',
] as const

export const GXP_IMPACTS = ['unassessed', 'none', 'assessed_no_risk', 'yes'] as const
export const DEVIATION_DECISIONS = ['not_required', 'raised', 'pending'] as const

export interface ResolveFormValues {
  summary: string
  category: string
  rootCause: string
  gxpImpact: string
  deviationDecision: string
  deviationRef: string
  duplicateOf: string
  reason: string
}

export type ResolveField = keyof ResolveFormValues

/** Cause / fix / prevention template the summary is prefilled with. */
export function summaryTemplate(labels: { cause: string; fix: string; prevention: string }): string {
  return `${labels.cause}: \n${labels.fix}: \n${labels.prevention}: `
}

export function initialResolveValues(
  ticket: { gxpImpact?: string | null; deviationDecision?: string | null; deviationRef?: string | null },
  template: string,
): ResolveFormValues {
  return {
    summary: template,
    category: '',
    rootCause: '',
    gxpImpact: ticket.gxpImpact && ticket.gxpImpact !== 'unassessed' ? ticket.gxpImpact : '',
    deviationDecision: ticket.deviationDecision ?? '',
    deviationRef: ticket.deviationRef ?? '',
    duplicateOf: '',
    reason: '',
  }
}

/**
 * `TKT-04812`, `tkt 4812` or `4812` → `TKT-04812`; anything else → null.
 * The API resolves keys and numbers, so the dialog looks the ticket up by key.
 */
export function normalizeTicketKey(input: string): string | null {
  const m = /^\s*(?:tkt[\s-]*)?0*(\d{1,9})\s*$/i.exec(input)
  if (!m) return null
  return `TKT-${m[1].padStart(5, '0')}`
}

/** Field → i18n error key. Empty when the form may be submitted. */
export function validateResolve(
  values: ResolveFormValues,
  ticketType: string,
  template: string,
): Partial<Record<ResolveField, string>> {
  const errors: Partial<Record<ResolveField, string>> = {}
  const blank = (v: string) => v.trim().length === 0
  if (blank(values.summary) || values.summary.trim() === template.trim()) errors.summary = 'resolve.errors.summary'
  if (blank(values.category)) errors.category = 'resolve.errors.category'
  if (ticketType.toLowerCase() === 'defect' && blank(values.rootCause)) errors.rootCause = 'resolve.errors.rootCause'
  if (blank(values.gxpImpact) || values.gxpImpact === 'unassessed') errors.gxpImpact = 'resolve.errors.gxpImpact'
  if (values.deviationDecision === 'raised' && blank(values.deviationRef)) errors.deviationRef = 'resolve.errors.deviationRef'
  if (values.category === 'duplicate' && !normalizeTicketKey(values.duplicateOf)) errors.duplicateOf = 'resolve.errors.duplicateOf'
  if (blank(values.reason)) errors.reason = 'resolve.errors.reason'
  return errors
}

/** The `{ problems: string[] }` of a 422, else null. */
export function serverProblems(err: unknown): string[] | null {
  if (!(err instanceof ApiError) || err.status !== 422) return null
  const body = err.body as { problems?: unknown } | undefined
  if (body && Array.isArray(body.problems)) return body.problems.map(String)
  return [err.message]
}
